"use client";

import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { fetchWithCsrf } from "../../lib/csrfClient";

const MESSAGE_MAX_LENGTH = 2000;

const emptyForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  projectType: "",
  budget: "",
  timeline: "",
  message: "",
  maintenance: false,
};

const inputClasses =
  "w-full bg-[var(--card-gradient-from)] border border-[var(--border-color)] rounded-sm px-4 py-3 text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--brand-color)] focus:outline-none transition-colors";

const labelClasses =
  "block font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2";

export default function ContactPanel({ services = [] }) {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const projectTypes = services
    .filter((s) => s.showInDropdown !== false)
    .map((s) => s.title);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetchWithCsrf("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, maintenance: false }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData(emptyForm);
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "contact_form_submit", {
            event_category: "Contact",
            event_label: "Studio Home Inline Form",
          });
        }
      } else {
        const data = await response.json();
        setError(data.error || "Failed to send message");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="border border-[var(--border-color)] rounded-sm p-10 bg-[var(--card-gradient-from)]">
        <div className="flex items-center gap-3 mb-4">
          <Check className="w-5 h-5 text-[var(--brand-color)]" />
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--brand-color)]">
            Message received
          </p>
        </div>
        <p className="font-display text-2xl text-[var(--text-primary)] mb-3">
          Thanks. I read every message myself.
        </p>
        <p className="text-[var(--text-secondary)]">
          You will hear back from me within 24 hours, usually sooner.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" id="contact-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className={labelClasses}>
            Name *
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            className={inputClasses}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="email" className={labelClasses}>
            Email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            className={inputClasses}
            placeholder="you@company.com"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="company" className={labelClasses}>
            Business
          </label>
          <input
            id="company"
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            className={inputClasses}
            placeholder="Business or project name"
          />
        </div>
        <div>
          <label htmlFor="projectType" className={labelClasses}>
            What do you need?
          </label>
          <select
            id="projectType"
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            className={inputClasses}
          >
            <option value="">Not sure yet</option>
            {projectTypes.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="message" className={labelClasses}>
          Tell me about it *
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          maxLength={MESSAGE_MAX_LENGTH}
          value={formData.message}
          onChange={handleChange}
          className={inputClasses}
          placeholder="What does your business do, and what should this website accomplish?"
        />
        <p className="mt-1 font-mono text-xs text-[var(--text-muted)] text-right">
          {formData.message.length}/{MESSAGE_MAX_LENGTH}
        </p>
      </div>

      {error && (
        <p className="font-mono text-sm text-[var(--brand-color)] border border-[var(--brand-color)]/40 rounded-sm px-4 py-3">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="inline-flex items-center gap-2 bg-[var(--brand-color)] text-[var(--bg-primary)] font-semibold rounded-sm px-8 py-4 hover:bg-[var(--brand-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {isSubmitting ? "Sending..." : "Send it over"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}
